/**
 * Full-screen playtest board (spec-aligned rewrite).
 *
 * Wires to playtestEngine.ts. Phases:
 *   coin-flip → mulligan-p1 → mulligan-p2 → preparation → playing
 *     → recover → planning-p1 → planning-p2 → (next round) playing → finished
 *
 * Setup phases (coin-flip / mulligan / prep) reuse existing CoinFlipDialog,
 * MulliganView, PrepPhaseView. Active phases drive the new engine.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import type { Card } from "@/types/card";
import type { GameState, PendingPrompt, PassAndPlayState } from "@/types/game";
import { Button } from "@/components/ui/Button";
import { useStore } from "@/hooks/useStore";
import { useAuth } from "@/hooks/useAuth";
import {
  pushLog,
  pass as enginePass,
  proceedToPlanning,
  planningConfirm,
  confirmPendingPlay,
  activateCounter as engineActivateCounter,
  keepCounter as engineKeepCounter,
  discardAndDrawCounter as engineDiscardAndDrawCounter,
  adjustInstanceValue,
  takeDamage as engineTakeDamage,
  resolveCombat,
  setPendingPrompt,
  setPendingPlay,
  confirmQuickLearner,
  confirmZeusWrathbrand,
  confirmFuelInferno,
  confirmLoadoutWeapons,
  confirmThrustingRelease,
  confirmSpartacusOpening,
  expendToken,
  passReaction,
  popupTakeDamage,
  pendingAttackIsPiercing,
  drawCards,
  passActionChain,
  triggerStartOfRoundEffects,
  staminaCostOf,
  confirmAddBuffWeaponBlockToNextDefend,
  confirmBloodforgedPlate,
  confirmBloodiedEdge,
  confirmPickDiscardForBonus,
  confirmSearchDeckByFilter,
  confirmPickHandDiscard,
  confirmVengeanceAttack,
  dismissVengeanceAttack,
  activateItemCard,
} from "@/game/playtestEngine";
import { setActiveCardMap } from "@/game/engineTrace";
import { CoinFlipDialog } from "@/components/playtest/CoinFlipDialog";
import { MulliganView } from "@/components/playtest/MulliganView";
import { PrepPhaseView } from "@/components/playtest/PrepPhaseView";
import { PlayerPanel } from "@/components/playtest/PlayerPanel";
import { BoardTopBar } from "@/components/playtest/BoardTopBar";
import { LogDrawer } from "@/components/playtest/LogDrawer";
import { EngineTracePanel } from "@/components/playtest/EngineTracePanel";
import { CombatDock } from "@/components/playtest/CombatDock";
import { ActiveCombatPopup } from "@/components/playtest/ActiveCombatPopup";
import { CardActionPopup } from "@/components/playtest/CardActionPopup";
import {
  PendingPlayDialog,
  CounterActivateDialog,
  CounterActivatedDialog,
} from "@/components/playtest/PlaytestDialogs";
import { SpecialPrompts } from "@/components/playtest/SpecialPrompts";
import { SpartaPromptDialog } from "@/components/playtest/CombatDialogs";
import { ActionChainOverlay } from "@/components/playtest/ActionChainOverlay";
import { PassScreen } from "@/components/playtest/PassScreen";
import { ReactionPromptBadge } from "@/components/playtest/ReactionPromptBadge";
import { toast } from "sonner";

interface LocationState {
  game?: GameState;
}

export function PlaytestBoard() {
  const location = useLocation();
  const incoming = (location.state as LocationState | null)?.game ?? null;
  const { state } = useStore();
  const { profile } = useAuth();
  const isAdmin = profile?.role === "admin";
  const [game, setGame] = useState<GameState | null>(incoming);
  const [logOpen, setLogOpen] = useState(false);
  // Engine Trace drawer — admin-only debugging panel showing every state
  // change emitted by the engine, with file:line source locations.
  const [traceOpen, setTraceOpen] = useState(false);
  // Hand-card click → opens the action popup with full details + buttons.
  /**
   * Generalized action popup state — used for hand cards AND for cards on
   * the board (sig moves, equipment, armor). The `source` discriminates which
   * engine API to call.
   */
  const [handPopup, setHandPopup] = useState<{
    playerNum: 1 | 2;
    instanceId: string;
    card: Card;
    source:
      | "hand"
      | "sig"
      | "equip"
      | "armor"
      | "champion"
      | "stance"
      | "played"
      | "item";
    slotIdx?: number;
  } | null>(null);
  /** Which player's discard pile is currently being viewed. null = closed. */
  const [discardViewer, setDiscardViewer] = useState<1 | 2 | null>(null);

  /**
   * Tabletop-mode per-player hand reveal flags. In tabletop active-play both
   * hands sit face-down by default — each player taps their own face-down
   * stack to flip it up, and a Hide button (rendered by FloatingHand) flips
   * it back down. These flags are ignored outside tabletop active-play, where
   * the viewer-based `hideHandFor` rule applies instead.
   */
  const [p1HandRevealed, setP1HandRevealed] = useState(false);
  const [p2HandRevealed, setP2HandRevealed] = useState(false);

  useEffect(() => {
    setGame(incoming);
  }, [incoming]);

  /**
   * Auto-hide both hands whenever the game leaves an active-play phase
   * (e.g. into planning / mulligan / preparation). The next time it comes
   * back to playing, both hands will start face-down so each round opens
   * with the correct privacy default.
   */
  useEffect(() => {
    if (!game) return;
    const ph = game.phase ?? "playing";
    if (ph !== "playing" && ph !== "recover" && ph !== "finished") {
      if (p1HandRevealed) setP1HandRevealed(false);
      if (p2HandRevealed) setP2HandRevealed(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.phase]);

  const cardMap = useMemo(() => {
    const m = new Map<string, Card>();
    for (const c of state.cards) m.set(c.id, c);
    return m;
  }, [state.cards]);

  // Register the active card map with the trace pipeline so engine emits
  // can resolve cardId↔cardName and the debug panel can render "Open card"
  // links that jump to /cards/<id>. Cleared on unmount so other pages don't
  // see a stale map.
  useEffect(() => {
    setActiveCardMap(cardMap);
    return () => setActiveCardMap(null);
  }, [cardMap]);

  // ─── Pass-and-play handoff machinery ─────────────────────────────────
  //
  // "Who acts now" tracks which player would normally have control. When this
  // changes and differs from the current viewer, set pendingPassTo so the
  // PassScreen overlay forces a physical handoff before the new player sees
  // the board.
  //
  // Special-case the per-player prep flow: in pass-and-play mode we walk
  // through P1's prep panel → pass → P2's prep panel → begin (rather than the
  // default shared two-panel view that leaks both hands).
  const [prepActor, setPrepActor] = useState<1 | 2>(1);
  const prevActorRef = useRef<1 | 2 | null>(null);

  function whoActsNow(g: GameState): 1 | 2 {
    const ph = g.phase ?? "playing";
    if (ph === "mulligan-p1") return 1;
    if (ph === "mulligan-p2") return 2;
    if (ph === "planning-p1") return 1;
    if (ph === "planning-p2") return 2;
    if (ph === "preparation") return prepActor;
    if (ph === "coin-flip") return g.coinFlipWinner ?? 1;
    // playing / recover / finished — use priority.
    return g.priority;
  }

  function setPassAndPlay(
    patch: Partial<PassAndPlayState>,
  ) {
    setGame((g) => {
      if (!g || !g.passAndPlay) return g;
      return {
        ...g,
        passAndPlay: { ...g.passAndPlay, ...patch },
      };
    });
  }

  /**
   * In tabletop layout, during the active-play phases (playing / recover /
   * finished) the device is sitting on the table between both players — there
   * is no privacy concern and no "pass the device" gesture. The PassScreen
   * overlay only makes sense for phases that show a single player's private
   * info (mulligan, planning, preparation, coin-flip).
   */
  function isTabletopActivePlay(g: GameState): boolean {
    if (!g.passAndPlay) return false;
    if (g.passAndPlay.layout !== "tabletop") return false;
    const ph = g.phase ?? "playing";
    return ph === "playing" || ph === "recover" || ph === "finished";
  }

  // Trigger handoff whenever the acting player changes.
  useEffect(() => {
    if (!game) return;
    if (!game.passAndPlay) return;
    const actor = whoActsNow(game);
    if (prevActorRef.current === actor) return;
    prevActorRef.current = actor;
    // Tabletop during active play: never queue a handoff — the device is
    // shared and both seats see the screen.
    if (isTabletopActivePlay(game)) return;
    if (game.passAndPlay.viewerNum !== actor && game.passAndPlay.pendingPassTo !== actor) {
      setPassAndPlay({ pendingPassTo: actor });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.priority, game?.phase, game?.coinFlipWinner, prepActor]);

  function handlePassScreenReady() {
    if (!game?.passAndPlay) return;
    const target = game.passAndPlay.pendingPassTo;
    if (target === null) return;
    // Promote target → viewer. Clear the prompt and reset declined-reaction
    // tracking so the new viewer can be prompted fresh.
    setPassAndPlay({
      viewerNum: target,
      pendingPassTo: null,
      declinedReactionEntryIds: [],
    });
  }

  /** Optional: parent components call this to force a handoff (e.g. when a
   *  reaction prompt is accepted). The pass-screen will appear next render. */
  function passDeviceTo(target: 1 | 2) {
    if (!game?.passAndPlay) return;
    if (game.passAndPlay.viewerNum === target) return;
    setPassAndPlay({ pendingPassTo: target });
  }

  // Auto-resolve combat ONLY after the reaction window has explicitly closed
  // (both players passed). The engine's passReaction() already calls
  // resolveCombat when consecutivePasses >= 2, so this effect must NOT
  // resolve while a reactionChain is open or while the defender still owes
  // a block. Otherwise combat resolves the instant a defend lands.
  useEffect(() => {
    if (!game) return;
    if (game.status !== "playing") return;
    const slots = game.combatSlots ?? [];
    if (slots.length === 0) return;
    const hasUnresolved = slots.some((s) => !s.resolved);
    if (!hasUnresolved) return;
    // Reaction window open → engine drives resolution via passReaction.
    if (game.reactionChain != null) return;
    // Defender still owes a block (no reactionChain yet, no blockCard).
    if (game.subPhase === "defender-pre-block") return;
    const stillNeedsBlock = slots.some(
      (s) => !s.resolved && s.blockCard === null,
    );
    if (stillNeedsBlock) return;
    // All blocks attached, no chain open → safe to resolve.
    setGame((g) => (g ? resolveCombat(g, cardMap) : g));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.combatSlots, game?.subPhase, game?.status, game?.reactionChain]);

  if (!game) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-6">
        <div className="gm-panel max-w-md space-y-3 p-6 text-center">
          <h1 className="font-display text-lg font-semibold">No active match</h1>
          <p className="text-sm text-[#94a3b8]">
            Start a solo match first to view the full-screen board.
          </p>
          <Link to="/playtest/solo">
            <Button>Go to Solo Playtest</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Pass-and-play overlay — short-circuits everything else until acknowledged.
  // Skipped entirely for tabletop layout during active-play phases (the device
  // is shared on the table; no handoff needed).
  if (
    game.passAndPlay &&
    game.passAndPlay.pendingPassTo !== null &&
    !isTabletopActivePlay(game)
  ) {
    const targetName = game.players[game.passAndPlay.pendingPassTo].name;
    return (
      <PassScreen
        toPlayerName={targetName}
        layout={game.passAndPlay.layout}
        onReady={handlePassScreenReady}
      />
    );
  }

  const phase = game.phase ?? "playing";

  // ─── Setup phase handlers (re-used from prior board) ──────────────────────

  function handleCoinFlipChoice(goFirst: boolean) {
    if (!game) return;
    const winner = game.coinFlipWinner ?? 1;
    const priority: 1 | 2 = goFirst ? winner : winner === 1 ? 2 : 1;
    setGame({
      ...game,
      priority,
      aggressor: priority,
      originalAggressor: priority,
      phase: "mulligan-p1",
      coinFlipRequired: false,
    });
  }

  function handleMulligan(playerNum: 1 | 2, newHand: string[], newDeck: string[]) {
    if (!game) return;
    const player = game.players[playerNum];
    // Keep handInstances synced with `hand` ordering. Each card id gets a
    // fresh per-instance id when it enters the hand from a mulligan.
    const handInstances = newHand.map((cid, i) => ({
      id: `${playerNum}-mh-${Date.now()}-${i}`,
      cardId: cid,
    }));
    let next: GameState = {
      ...game,
      players: {
        ...game.players,
        [playerNum]: {
          ...player,
          hand: newHand,
          deckCards: newDeck,
          handInstances,
        },
      } as GameState["players"],
    };
    next = pushLog(next, player.name, "Kept opening hand");
    next.phase = playerNum === 1 ? "mulligan-p2" : "preparation";
    setGame(next);
  }

  function handlePlaceStance(playerNum: 1 | 2, cardDbId: string | null) {
    if (!game) return;
    const player = game.players[playerNum];
    setGame({
      ...game,
      players: {
        ...game.players,
        [playerNum]: { ...player, stanceCardId: cardDbId },
      } as GameState["players"],
    });
  }

  function handlePrepReady(playerNum: 1 | 2) {
    if (!game) return;
    const player = game.players[playerNum];
    let next: GameState = {
      ...game,
      players: {
        ...game.players,
        [playerNum]: { ...player, prepReady: true },
      } as GameState["players"],
    };
    if (player.stanceCardId) {
      const stanceCard = cardMap.get(player.stanceCardId);
      next = pushLog(next, player.name, `Placed stance: ${stanceCard?.name ?? "?"}`);
    } else {
      next = pushLog(next, player.name, "Skipped stance");
    }
    setGame(next);
    // In pass-and-play, advance prepActor so the next player is prompted to
    // pass-and-play their own prep step. The whoActsNow effect will trigger
    // a PassScreen.
    if (game.passAndPlay && playerNum === 1 && !game.players[2].prepReady) {
      setPrepActor(2);
    }
  }

  function handleBeginMatch() {
    if (!game) return;
    function strip(p: GameState["players"][1]) {
      if (!p.stanceCardId) return p;
      return {
        ...p,
        hand: p.hand.filter((id) => id !== p.stanceCardId),
        handInstances: p.handInstances.filter((h) => h.cardId !== p.stanceCardId),
      };
    }
    let next: GameState = {
      ...game,
      phase: "playing",
      subPhase: "aggressor-pre-attack",
      players: {
        1: strip(game.players[1]),
        2: strip(game.players[2]),
      } as GameState["players"],
    };
    next = pushLog(next, "system", `Round ${next.round} begins.`);
    // Round 1 start-of-play-phase passives (Aegis, Spartacus Opening Advantage).
    next = triggerStartOfRoundEffects(next, cardMap);
    setGame(next);
  }

  // ─── Active play handlers ────────────────────────────────────────────────

  function handlePass() {
    if (!game) return;
    setGame(enginePass(game, cardMap));
  }

  function handleAdjustHealth(playerNum: 1 | 2, delta: number) {
    if (!game) return;
    const player = game.players[playerNum];
    const newHp = Math.max(0, Math.min(player.maxHealth, player.health + delta));
    setGame({
      ...game,
      players: {
        ...game.players,
        [playerNum]: { ...player, health: newHp },
      } as GameState["players"],
    });
  }

  function handleAdjustStamina(playerNum: 1 | 2, delta: number) {
    if (!game) return;
    const player = game.players[playerNum];
    const newSt = Math.max(0, Math.min(20, player.stamina + delta));
    setGame({
      ...game,
      players: {
        ...game.players,
        [playerNum]: { ...player, stamina: newSt },
      } as GameState["players"],
    });
  }

  // Hand → click → queue a pending play (the engine then asks for confirm).
  // For MVP: legacy isPlayableAttack still gates clicks; the engine confirms.
  function handlePlayHandCard(playerNum: 1 | 2, cardDbId: string) {
    if (!game) return;
    if (game.priority !== playerNum) {
      toast.error("It's not your turn.");
      return;
    }
    const card = cardMap.get(cardDbId);
    if (!card) return;
    // Find the matching hand instance.
    const inst = game.players[playerNum].handInstances.find((h) => h.cardId === cardDbId);
    if (!inst) return;
    // Default to ad-attack if it has powerValue, else ad-defend.
    const power = Number(card.attackDefendFields?.powerValue ?? 0);
    const type = power > 0 ? "ad-attack" : "ad-defend";
    setGame(
      setPendingPlay(game, {
        type,
        playerNum,
        playCard: inst,
        cost: Number(card.attackDefendFields?.staminaCost ?? 0),
      }),
    );
  }

  // Hand-hover menu: explicit Atk/Def picker.
  function handleHandPlay(
    playerNum: 1 | 2,
    instanceId: string,
    cardDbId: string,
    mode: "attack" | "defend",
  ) {
    if (!game) return;
    if (game.priority !== playerNum) {
      toast.error("It's not your turn.");
      return;
    }
    const card = cardMap.get(cardDbId);
    if (!card) return;
    const inst = game.players[playerNum].handInstances.find((h) => h.id === instanceId);
    if (!inst) return;
    setGame(
      setPendingPlay(game, {
        type: mode === "attack" ? "ad-attack" : "ad-defend",
        playerNum,
        playCard: inst,
        cost: Number(card.attackDefendFields?.staminaCost ?? 0),
      }),
    );
  }

  function handleHandStance(playerNum: 1 | 2, instanceId: string, cardDbId: string) {
    if (!game) return;
    const card = cardMap.get(cardDbId);
    if (!card) return;
    const inst = game.players[playerNum].handInstances.find((h) => h.id === instanceId);
    if (!inst) return;
    setGame(
      setPendingPlay(game, {
        type: "play-stance",
        playerNum,
        playCard: inst,
        cost: 0,
      }),
    );
  }

  function handleHandUtility(playerNum: 1 | 2, instanceId: string, cardDbId: string) {
    if (!game) return;
    const card = cardMap.get(cardDbId);
    if (!card) return;
    const inst = game.players[playerNum].handInstances.find((h) => h.id === instanceId);
    if (!inst) return;
    setGame(
      setPendingPlay(game, {
        type: "play-utility",
        playerNum,
        playCard: inst,
        cost: 0,
      }),
    );
  }

  function handleHandCounter(playerNum: 1 | 2, instanceId: string, _cardDbId: string) {
    if (!game) return;
    if (game.priority !== playerNum) {
      toast.error("It's not your turn.");
      return;
    }
    // openCounterDialog opens the amber confirm dialog.
    const inst = game.players[playerNum].handInstances.find((h) => h.id === instanceId);
    if (!inst) return;
    setGame({
      ...game,
      counterDialog: { playerNum, card: inst, cardDbId: inst.cardId },
    });
  }

  function handleHandDiscard(playerNum: 1 | 2, instanceId: string, _cardDbId: string) {
    if (!game) return;
    // Discard a single hand instance (manual discard).
    const player = game.players[playerNum];
    const inst = player.handInstances.find((h) => h.id === instanceId);
    if (!inst) return;
    const newHandInstances = player.handInstances.filter((h) => h.id !== instanceId);
    const newHand = newHandInstances.map((h) => h.cardId);
    let next: GameState = {
      ...game,
      players: {
        ...game.players,
        [playerNum]: {
          ...player,
          handInstances: newHandInstances,
          hand: newHand,
          discard: [...player.discard, inst.cardId],
        },
      } as GameState["players"],
    };
    next = pushLog(next, player.name, `Discarded ${cardMap.get(inst.cardId)?.name ?? "card"}`);
    setGame(next);
  }

  function handlePlaySig(playerNum: 1 | 2, cardDbId: string) {
    if (!game) return;
    const slotIdx = game.players[playerNum].sigMoveSlots.findIndex(
      (s) => s.cardId === cardDbId && !s.used,
    );
    if (slotIdx < 0) return;
    handleSigSlot(playerNum, slotIdx, "attack");
  }

  /** Generalized sig-move dispatch (used by the action popup). */
  function handleSigSlot(
    playerNum: 1 | 2,
    slotIdx: number,
    mode: "attack" | "defend" | "utility",
  ) {
    if (!game) return;
    if (game.priority !== playerNum) {
      toast.error("It's not your turn.");
      return;
    }
    const slot = game.players[playerNum].sigMoveSlots[slotIdx];
    if (!slot || slot.used) return;
    const card = cardMap.get(slot.cardId);
    if (!card) return;
    // Read cost from whichever field block applies. Quick Learner copies
    // place non-sig cards (A/D, Utility, Stance, Equipment) into the sig slot,
    // so reading only signatureMoveFields would zero out the cost. The dialog
    // also re-runs previewEffectiveCost from the slot, which factors in
    // stance/buff reductions correctly for any card type.
    const cost = staminaCostOf(card);
    const ppType =
      mode === "attack" ? "sig-attack" : mode === "defend" ? "sig-defend" : "sig-utility";
    setGame(setPendingPlay(game, { type: ppType, playerNum, slotIdx, cost }));
  }

  /** Quick Learner copy dispatch — the sig slot houses a copy of a card whose
   *  cardType is not Signature Move; play it as its actual card type. */
  function handleQlSigSlot(
    playerNum: 1 | 2,
    slotIdx: number,
    mode: "attack" | "defend" | "utility" | "stance",
  ) {
    if (!game) return;
    if (game.priority !== playerNum) {
      toast.error("It's not your turn.");
      return;
    }
    const slot = game.players[playerNum].sigMoveSlots[slotIdx];
    if (!slot || slot.used) return;
    const card = cardMap.get(slot.cardId);
    if (!card) return;
    const cost = staminaCostOf(card);
    const ct = card.cardType;
    let ppType:
      | "qlsig-ad-attack"
      | "qlsig-ad-defend"
      | "qlsig-utility"
      | "qlsig-stance"
      | "qlsig-equip-attack"
      | "qlsig-equip-defend";
    if (ct === "Attack/Defend") {
      ppType = mode === "attack" ? "qlsig-ad-attack" : "qlsig-ad-defend";
    } else if (ct === "Equipment") {
      ppType = mode === "attack" ? "qlsig-equip-attack" : "qlsig-equip-defend";
    } else if (ct === "Utility") {
      ppType = "qlsig-utility";
    } else if (ct === "Stance") {
      ppType = "qlsig-stance";
    } else {
      toast.error(`Cannot play ${card.name} (${ct}) from sig slot.`);
      return;
    }
    setGame(setPendingPlay(game, { type: ppType, playerNum, slotIdx, cost }));
  }

  function handleTapEquipment(playerNum: 1 | 2, slotIdx: number) {
    handleEquipSlot(playerNum, slotIdx, "attack");
  }

  /** Generalized equipment dispatch (used by the action popup). */
  function handleEquipSlot(
    playerNum: 1 | 2,
    slotIdx: number,
    mode: "attack" | "defend",
  ) {
    if (!game) return;
    if (game.priority !== playerNum) {
      toast.error("Tap during your turn.");
      return;
    }
    const slot = game.players[playerNum].equipSlots[slotIdx];
    if (!slot?.cardId || slot.tapped) return;
    const card = cardMap.get(slot.cardId);
    if (!card) return;
    const cost = Number(card.equipmentFields?.staminaCost ?? 0);
    const ppType = mode === "attack" ? "equip-attack" : "equip-defend";
    setGame(setPendingPlay(game, { type: ppType, playerNum, slotIdx, cost }));
  }

  /** Armor block confirmation flow (mirrors the equipment defend flow). */
  function handleArmorBlock(playerNum: 1 | 2) {
    if (!game) return;
    if (game.priority !== playerNum) {
      toast.error("It's not your turn to defend.");
      return;
    }
    const p = game.players[playerNum];
    if (!p.armorActive || !p.armorCardId) {
      toast.error("Armor is unavailable.");
      return;
    }
    setGame(setPendingPlay(game, { type: "armor-block", playerNum, cost: 0 }));
  }

  
  function handleConfirmPendingPlay() {
    if (!game) return;
    setGame(confirmPendingPlay(game, cardMap));
  }

  function handleCancelPendingPlay() {
    if (!game) return;
    setGame(setPendingPlay(game, null));
  }

  // Counter dialog handlers
  function handleActivateCounter() {
    if (!game?.counterDialog) return;
    setGame(engineActivateCounter(game, game.counterDialog.playerNum, game.counterDialog.card.id, cardMap));
  }
  function handleCancelCounter() {
    if (!game) return;
    setGame({ ...game, counterDialog: null });
  }
  function handleKeepCounter() {
    if (!game?.counterActivatedDialog) return;
    setGame(engineKeepCounter(game, game.counterActivatedDialog.playerNum, game.counterActivatedDialog.card.id));
  }
  function handleDiscardAndDrawCounter() {
    if (!game?.counterActivatedDialog) return;
    setGame(engineDiscardAndDrawCounter(game, game.counterActivatedDialog.playerNum, game.counterActivatedDialog.card.id));
  }

  // CombatDock handlers
  function handleAdjustValue(
    n: 1 | 2,
    instanceId: string,
    cardDbId: string,
    mode: "attack" | "defend",
    delta: number,
  ) {
    if (!game) return;
    const card = cardMap.get(cardDbId);
    if (!card) return;
    setGame(adjustInstanceValue(game, n, instanceId, card, mode, delta));
  }

  function handleTakeDamage(defenderNum: 1 | 2) {
    if (!game) return;
    setGame(engineTakeDamage(game, defenderNum, cardMap));
  }

  // Special prompt handlers — for now these just clear the prompt.
  // The dedicated kind-by-kind effect logic lives inside the engine for the
  // most common cases; for now we let the prompt-confirm clear and log.
  function handleUpdatePrompt(next: PendingPrompt | null) {
    if (!game) return;
    setGame(setPendingPrompt(game, next));
  }
  function handleConfirmPrompt(
    result?:
      | { kind: "quick-learner"; pickedCardDbId: string }
      | { kind: "zeus-wrathbrand"; selectedSlots: number[] }
      | { kind: "fuel-inferno"; focusToSpend: number }
      | { kind: "bloodforged-activate"; countersToSpend: number }
      | { kind: "spartacus-opening"; choice: "activate" | "decline" | "stamina" | "draw" }
      | { kind: "single-disarm"; slotIdx: number | null }
      | { kind: "thrusting-release"; slotIdx: number | null }
      | { kind: "stance-swap"; swap: boolean }
      | { kind: "pick-loadout-weapons"; selectedSlots: number[] }
      | { kind: "pick-weapon"; slotIdx: number | null }
      | { kind: "pick-damaging-attacks"; selectedSlotIds: string[] }
      // Phase 3
      | { kind: "search-deck-by-filter"; selectedInstanceIds: string[] }
      | { kind: "pick-hand-discard"; selectedInstanceIds: string[] }
      | { kind: "pick-discard-for-bonus"; selectedInstanceIds: string[] }
      | { kind: "vengeance-attack"; accepted: boolean },
  ) {
    if (!game) return;
    if (!result) return setGame(setPendingPrompt(game, null));
    switch (result.kind) {
      case "quick-learner":
        setGame(confirmQuickLearner(game, result.pickedCardDbId, cardMap));
        return;
      case "zeus-wrathbrand":
        setGame(confirmZeusWrathbrand(game, result.selectedSlots, cardMap));
        return;
      case "fuel-inferno":
        setGame(confirmFuelInferno(game, result.focusToSpend, cardMap));
        return;
      case "bloodforged-activate":
        setGame(confirmBloodforgedPlate(game, result.countersToSpend, cardMap));
        return;
      case "pick-loadout-weapons":
        setGame(confirmLoadoutWeapons(game, result.selectedSlots, cardMap));
        return;
      case "thrusting-release":
        setGame(confirmThrustingRelease(game, result.slotIdx, cardMap));
        return;
      case "spartacus-opening":
        setGame(confirmSpartacusOpening(game, result.choice, cardMap));
        return;
      case "pick-weapon":
        setGame(confirmAddBuffWeaponBlockToNextDefend(game, result.slotIdx, cardMap));
        return;
      case "pick-damaging-attacks":
        setGame(confirmBloodiedEdge(game, result.selectedSlotIds, cardMap));
        return;
      // Phase 3
      case "search-deck-by-filter":
        setGame(confirmSearchDeckByFilter(game, result.selectedInstanceIds, cardMap));
        return;
      case "pick-hand-discard":
        setGame(confirmPickHandDiscard(game, result.selectedInstanceIds, cardMap));
        return;
      case "pick-discard-for-bonus":
        setGame(confirmPickDiscardForBonus(game, result.selectedInstanceIds, cardMap));
        return;
      case "vengeance-attack":
        setGame(result.accepted ? confirmVengeanceAttack(game, cardMap) : dismissVengeanceAttack(game));
        return;
      default:
        // Other prompts (single-disarm, stance-swap) are still wired through
        // their own handlers — just dismiss.
        setGame(setPendingPrompt(game, null));
    }
  }

  // SPARTA defend prompt — apply +N block to the live defend slot that was
  // just played (not a queued buff for the NEXT defend). Tap selected weapons.
  function handleSpartaConfirm(
    prompt: Extract<PendingPrompt, { kind: "sparta-defend" }>,
    selected: number[],
  ) {
    if (!game) return;
    const defenderNum = prompt.playerNum;
    let p = game.players[defenderNum];
    const slots = p.equipSlots.slice();
    for (const idx of selected) {
      if (slots[idx]?.cardId) slots[idx] = { ...slots[idx], tapped: true };
    }
    const bonus = selected.length * prompt.extraBlockBonus;
    p = { ...p, equipSlots: slots };
    let next: GameState = {
      ...game,
      players: {
        ...game.players,
        [defenderNum]: p,
      } as GameState["players"],
      pendingPrompt: null,
    };
    // Find the most recently played defend zone slot (the just-played This is
    // Sparta defend). Apply the bonus directly to its adValueOverrides so the
    // live block reflects the bonus right now.
    if (bonus > 0) {
      const defendSlots = p.attackDefendZone.filter((z) => z.side === "defend");
      const latestDefend = defendSlots[defendSlots.length - 1];
      const defendCard = latestDefend ? cardMap.get(latestDefend.cardId) : null;
      if (latestDefend && defendCard) {
        next = adjustInstanceValue(
          next,
          defenderNum,
          latestDefend.id,
          defendCard,
          "defend",
          bonus,
        );
      }
    }
    next = pushLog(
      next,
      p.name,
      `This is Sparta! +${bonus} block from ${selected.length} slot(s)`,
    );
    setGame(next);
  }
  function handleSpartaCancel() {
    if (!game) return;
    setGame({ ...game, pendingPrompt: null });
  }

  // Recover phase → click "Continue to Planning"
  function handleProceedToPlanning() {
    if (!game) return;
    setGame(proceedToPlanning(game));
  }

  // Planning phase → confirm
  function handlePlanningConfirm(playerNum: 1 | 2, toDiscardInstanceIds: string[]) {
    if (!game) return;
    // Pass cardMap so beginRound can fire start-of-round hooks (Aegis,
    // Spartacus Opening Advantage, etc.).
    setGame(planningConfirm(game, playerNum, toDiscardInstanceIds, cardMap));
  }

  // ─── Phase rendering ─────────────────────────────────────────────────────

  if (phase === "coin-flip") {
    const championNum = game.players[1].championCardId;
    const champCard = championNum ? cardMap.get(championNum) : null;
    const speed = parseFloat(champCard?.championFields?.speed ?? "0") || 0;
    const winner = game.coinFlipWinner ?? 1;
    const winnerName = game.players[winner].name;
    return (
      <div className="min-h-screen bg-bg">
        <CoinFlipDialog
          championSpeed={speed}
          flipWinnerName={winnerName}
          onChoose={handleCoinFlipChoice}
        />
      </div>
    );
  }

  if (phase === "mulligan-p1" || phase === "mulligan-p2") {
    const playerNum: 1 | 2 = phase === "mulligan-p1" ? 1 : 2;
    return (
      <div className="min-h-screen bg-bg p-6">
        <MulliganView
          game={game}
          playerNum={playerNum}
          cardMap={cardMap}
          onKeep={(hand, deck) => handleMulligan(playerNum, hand, deck)}
        />
      </div>
    );
  }

  if (phase === "preparation") {
    return (
      <div className="min-h-screen bg-bg p-6">
        <PrepPhaseView
          game={game}
          cardMap={cardMap}
          onPlaceStance={handlePlaceStance}
          onReady={handlePrepReady}
          onBegin={handleBeginMatch}
          singlePlayerNum={game.passAndPlay ? prepActor : undefined}
        />
      </div>
    );
  }

  if (phase === "recover") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg p-6">
        <div className="gm-panel max-w-lg space-y-3 p-6 text-center">
          <h2 className="font-display text-lg font-semibold">Recover Phase</h2>
          <p className="text-sm text-[#94a3b8]">
            Round-end cleanup. Equipment untaps, items persist, hand carries over.
          </p>
          <Button onClick={handleProceedToPlanning}>Continue to Planning</Button>
        </div>
      </div>
    );
  }

  if (phase === "planning-p1" || phase === "planning-p2") {
    const planningNum: 1 | 2 = phase === "planning-p1" ? 1 : 2;
    return (
      <PlanningView
        game={game}
        playerNum={planningNum}
        cardMap={cardMap}
        onConfirm={(ids) => handlePlanningConfirm(planningNum, ids)}
      />
    );
  }

  // ─── Active board (phase === "playing" or "finished") ────────────────────

  const activePlayer = game.players[game.priority];
  const aggressorNum = game.aggressor ?? game.priority;

  // Pass-and-play viewer logic. When P+P is active:
  //   * stacked layout: the viewer's panel renders on the BOTTOM and the
  //     opponent's on the TOP (board flips on each handoff).
  //   * tabletop layout: P1 stays bottom, P2 stays top, but P2's panel
  //     container is rotated 180° so the seated opponent reads it
  //     right-side-up from across the table.
  // Hand visibility:
  //   * stacked / pre-game: opponent's hand is hidden (face-down) unless peek
  //     is on. The active viewer's hand is always visible.
  //   * tabletop active-play: BOTH hands start face-down. Each player
  //     independently lifts their own with the floating-hand reveal toggle.
  const isPassAndPlay = !!game.passAndPlay;
  const viewerNum: 1 | 2 = game.passAndPlay?.viewerNum ?? 1;
  const opponentNum: 1 | 2 = viewerNum === 1 ? 2 : 1;
  const layout = game.passAndPlay?.layout ?? "stacked";
  const peek = !!game.passAndPlay?.peek;
  const tabletopActive = isTabletopActivePlay(game);
  // Stacked: viewer always on bottom. Tabletop: P1 always bottom, P2 always top.
  const bottomNum: 1 | 2 = layout === "tabletop" ? 1 : viewerNum;
  const topNum: 1 | 2 = bottomNum === 1 ? 2 : 1;
  // hideHand for each player.
  //   * In solo (no P+P) — hands always shown.
  //   * Tabletop active-play — driven by per-player reveal state below.
  //   * Otherwise — hide opponent's hand from the current viewer.
  const hideHandFor = (n: 1 | 2): boolean => {
    if (!isPassAndPlay) return false;
    if (peek) return false;
    if (tabletopActive) {
      return n === 1 ? !p1HandRevealed : !p2HandRevealed;
    }
    return n !== viewerNum;
  };
  // Tabletop ALWAYS rotates the top (P2) half so the across-the-table seat
  // reads it right-side-up, regardless of who is the nominal viewer.
  const tabletopRotateTop = layout === "tabletop";
  const tabletopRotateBottom = false;

  // Reaction-prompt visibility. The engine sets `actionChain.reactor` to the
  // opponent's number when they have at least one legal reaction. In stacked
  // P+P we surface a small Yes/No badge on the active viewer's screen so they
  // know to pass the device. In tabletop active-play both players see the
  // device already — the reactor can play their reaction (or pass) directly
  // through their own combat dock controls, so the badge is suppressed.
  const actionChain = game.actionChain ?? null;
  const lastEntry = actionChain?.entries.length
    ? actionChain.entries[actionChain.entries.length - 1]
    : null;
  const showReactionPrompt =
    isPassAndPlay &&
    !tabletopActive &&
    actionChain != null &&
    actionChain.reactor === opponentNum &&
    game.priority === viewerNum &&
    lastEntry != null &&
    !(game.passAndPlay?.declinedReactionEntryIds ?? []).includes(lastEntry.id);

  // Helper: render a player panel with all its handlers wired.
  const renderPlayerPanel = (n: 1 | 2, side: "top" | "bottom") => (
    <PlayerPanel
      player={game.players[n]}
      cardMap={cardMap}
      isActive={game.priority === n}
      side={side}
      hideHand={hideHandFor(n)}
      handRotated={side === "top" ? tabletopRotateTop : tabletopRotateBottom}
      mirrorAsBottom={side === "top" && tabletopRotateTop}
      onAdjustHealth={(d) => handleAdjustHealth(n, d)}
      onAdjustStamina={(d) => handleAdjustStamina(n, d)}
      onHandCardClick={(iid, card) =>
        setHandPopup({ playerNum: n, instanceId: iid, card, source: "hand" })
      }
      onSlotCardClick={(kind, card, slotIdx, instanceId) =>
        setHandPopup({
          playerNum: n,
          // For played/item cards we have a real per-instance id from the
          // played/utility-in-play arrays — prefer that so future Activate
          // Ability handlers can target the exact instance. For
          // sig/equip/armor/champion/stance the instance "is" the card, so
          // fall back to card.id.
          instanceId: instanceId ?? card.id,
          card,
          source: kind,
          slotIdx,
        })
      }
      armorBlockable={
        game.subPhase === "defender-pre-block" &&
        game.priority === n &&
        game.players[n].armorActive &&
        !pendingAttackIsPiercing(game, n, cardMap)
      }
      onDrawCard={() => setGame(drawCards(game, n, 1))}
      onOpenDiscardPile={() => setDiscardViewer(n)}
      onExpendToken={(instanceId) => setGame(expendToken(game, n, instanceId, cardMap))}
      onToggleSelfReveal={
        tabletopActive
          ? () =>
              n === 1
                ? setP1HandRevealed((s) => !s)
                : setP2HandRevealed((s) => !s)
          : undefined
      }
    />
  );

  return (
    <div className="flex min-h-screen flex-col bg-bg">
      <BoardTopBar
        round={game.round}
        activePlayerName={activePlayer.name}
        isAggressor={game.priority === aggressorNum}
        logCount={game.log.length}
        onToggleLog={() => setLogOpen((s) => !s)}
        logOpen={logOpen}
        showTraceButton={isAdmin}
        onToggleTrace={() => setTraceOpen((s) => !s)}
        traceOpen={traceOpen}
      />

      {/* Snug flex-column layout: top bar / opponent hand peek / opponent
          board / combat dock / viewer board / viewer hand peek. Hands are
          fixed-positioned overlays anchored to the top/bottom edges; we
          reserve space here via padding so the panels never go behind them. */}
      <div
        className="flex flex-1 min-h-0 flex-col gap-1 px-2 pt-2 pb-2 sm:gap-2 sm:px-3"
        style={{
          // Reserve room for the tucked hand peeks (~30% of card height +
          // chevron chip + padding). Tucked card is ~h-28 (7rem) so the
          // visible sliver is roughly 2.25rem.
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 2.75rem + 2.5rem)",
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 2.5rem)",
        }}
      >
        {/* Top panel — opponent in stacked mode; canonical P2 in tabletop
            (rotated 180° so the seated opponent reads it right-side-up). */}
        <div
          className="flex-shrink-0"
          style={tabletopRotateTop ? { transform: "rotate(180deg)" } : undefined}
        >
          {renderPlayerPanel(topNum, "top")}
        </div>

        {/* Combat dock — fixed size, centered between the two boards. The dock
            height is locked so the zone never grows as attacks are played; new
            slots simply scroll inside the dock. ActiveCombatPopup uses
            `absolute inset-0` so it perfectly overlays this same fixed box. */}
        <div className="relative h-[28vh] min-h-[12rem] max-h-[20rem] flex-shrink-0">
          <CombatDock
            game={game}
            cardMap={cardMap}
            selfNum={1}
            onAdjustValue={handleAdjustValue}
            onTakeDamage={handleTakeDamage}
            onPass={handlePass}
            canPass={(() => {
              if (game.status !== "playing") return false;
              if (game.pendingPlay || game.pendingPrompt) return false;
              // Engine #3: when both players have passed without an attack,
              // the original aggressor is forced to play an attack — they
              // cannot pass again.
              if (
                game.mustAttackWarning &&
                game.priority === (game.originalAggressor ?? game.aggressor)
              ) {
                return false;
              }
              return true;
            })()}
          />
          {(() => {
            const slots = game.combatSlots ?? [];
            const hasUnresolved = slots.some((s) => !s.resolved);
            const showPopup =
              hasUnresolved &&
              (game.reactionChain != null || game.subPhase === "defender-pre-block");
            if (!showPopup) return null;
            return (
              <ActiveCombatPopup
                game={game}
                cardMap={cardMap}
                selfNum={game.priority}
                onPass={() => setGame(passReaction(game, cardMap))}
                onTakeDamage={() => {
                  const slot = (game.combatSlots ?? []).find((s) => !s.resolved);
                  if (!slot) return;
                  const def: 1 | 2 = slot.attackerNum === 1 ? 2 : 1;
                  setGame(popupTakeDamage(game, def, cardMap));
                }}
                onAdjustValue={handleAdjustValue}
              />
            );
          })()}
        </div>

        {/* Bottom panel — viewer's seat in stacked mode; canonical P1 in
            tabletop (rotated only if P1 is the OPPONENT, which only happens
            for symmetry in unusual configurations — normally false). */}
        <div
          className="flex-shrink-0"
          style={tabletopRotateBottom ? { transform: "rotate(180deg)" } : undefined}
        >
          {renderPlayerPanel(bottomNum, "bottom")}
        </div>
      </div>

      {/* Pass-and-play reaction prompt — small badge offering Yes/No when the
          opponent has a legal reaction. Yes → hand the device over so they can
          take their reaction window. No → dismiss for that chain entry only. */}
      {showReactionPrompt && lastEntry && (
        <ReactionPromptBadge
          opponentName={game.players[opponentNum].name}
          onAccept={() => passDeviceTo(opponentNum)}
          onDecline={() =>
            setPassAndPlay({
              declinedReactionEntryIds: [
                ...(game.passAndPlay?.declinedReactionEntryIds ?? []),
                lastEntry.id,
              ],
            })
          }
        />
      )}

      {/* Pending Play confirmation */}
      {game.pendingPlay && (
        <PendingPlayDialog
          pp={game.pendingPlay}
          game={game}
          cardMap={cardMap}
          onConfirm={handleConfirmPendingPlay}
          onCancel={handleCancelPendingPlay}
        />
      )}

      {/* Counter dialog flow */}
      {game.counterDialog && (() => {
        const card = cardMap.get(game.counterDialog.cardDbId);
        if (!card) return null;
        return (
          <CounterActivateDialog
            card={card}
            buffSummary={game.counterDialog.buffSummary}
            onConfirm={handleActivateCounter}
            onCancel={handleCancelCounter}
          />
        );
      })()}
      {game.counterActivatedDialog && (() => {
        const card = cardMap.get(game.counterActivatedDialog.cardDbId);
        if (!card) return null;
        return (
          <CounterActivatedDialog
            card={card}
            buffSummary={game.counterActivatedDialog.buffSummary}
            onDiscardAndDraw={handleDiscardAndDrawCounter}
            onKeep={handleKeepCounter}
          />
        );
      })()}

      {/* Free-reaction window indicator removed — the Pass button lives in
          ActiveCombatPopup, which is the canonical place to react during
          combat. The bottom bar created visual noise. */}

      {/* Action Chain overlay (general-purpose reaction window) */}
      {game.actionChain && game.actionChain.entries.length > 0 && (
        <ActionChainOverlay
          game={game}
          viewerNum={game.actionChain.reactor ?? game.priority}
          cardMap={cardMap}
          onPass={() => setGame(passActionChain(game, cardMap))}
        />
      )}

      {/* Special prompts (Spartacus, Zeus, Quick Learner, Single Disarm, etc.) */}
      <SpecialPrompts
        game={game}
        cardMap={cardMap}
        onUpdatePrompt={handleUpdatePrompt}
        onConfirmPrompt={handleConfirmPrompt}
      />

      {/* Legacy SPARTA defend prompt */}
      {game.pendingPrompt?.kind === "sparta-defend" && (
        <SpartaPromptDialog
          prompt={game.pendingPrompt}
          cardMap={cardMap}
          onConfirm={(sel) =>
            handleSpartaConfirm(
              game.pendingPrompt as Extract<PendingPrompt, { kind: "sparta-defend" }>,
              sel,
            )
          }
          onCancel={handleSpartaCancel}
        />
      )}

      {game.status === "finished" && game.winner && (
        <div className="border-t border-amber/40 bg-amber-500/10 px-3 py-3 text-center">
          <span className="font-display text-base font-semibold text-amber-soft">
            🏆 {game.players[game.winner].name} wins!
          </span>
        </div>
      )}

      <LogDrawer open={logOpen} log={game.log} onClose={() => setLogOpen(false)} />

      {/* Engine Trace — admin-only. Mounted unconditionally for admin so the
          subscription is active while the drawer is closed too (the panel
          itself returns null when !open, so non-admin users pay nothing). */}
      {isAdmin && (
        <EngineTracePanel open={traceOpen} onClose={() => setTraceOpen(false)} />
      )}

      {/* Discard pile viewer modal. */}
      {discardViewer && (
        <DiscardPileModal
          player={game.players[discardViewer]}
          cardMap={cardMap}
          onClose={() => setDiscardViewer(null)}
        />
      )}

      {handPopup && (
        <CardActionPopup
          card={handPopup.card}
          instanceId={handPopup.instanceId}
          playerNum={handPopup.playerNum}
          game={game}
          cardMap={cardMap}
          source={handPopup.source}
          slotIdx={handPopup.slotIdx}
          onClose={() => setHandPopup(null)}
          // Hand actions
          onPlayAttack={(iid, cid) => {
            handleHandPlay(handPopup.playerNum, iid, cid, "attack");
            setHandPopup(null);
          }}
          onPlayDefend={(iid, cid) => {
            handleHandPlay(handPopup.playerNum, iid, cid, "defend");
            setHandPopup(null);
          }}
          onActivateCounter={(iid, cid) => {
            handleHandCounter(handPopup.playerNum, iid, cid);
            setHandPopup(null);
          }}
          onPlayUtility={(iid, cid) => {
            handleHandUtility(handPopup.playerNum, iid, cid);
            setHandPopup(null);
          }}
          onPlayStance={(iid, cid) => {
            handleHandStance(handPopup.playerNum, iid, cid);
            setHandPopup(null);
          }}
          onDiscardCard={(iid, cid) => {
            handleHandDiscard(handPopup.playerNum, iid, cid);
            setHandPopup(null);
          }}
          // Sig-move actions
          onPlaySigAttack={(idx) => {
            handleSigSlot(handPopup.playerNum, idx, "attack");
            setHandPopup(null);
          }}
          onPlaySigDefend={(idx) => {
            handleSigSlot(handPopup.playerNum, idx, "defend");
            setHandPopup(null);
          }}
          onPlaySigUtility={(idx) => {
            handleSigSlot(handPopup.playerNum, idx, "utility");
            setHandPopup(null);
          }}
          // Quick Learner copy actions — sig slot houses a non-sig card type.
          onPlayQlSigAsAttack={(idx) => {
            handleQlSigSlot(handPopup.playerNum, idx, "attack");
            setHandPopup(null);
          }}
          onPlayQlSigAsDefend={(idx) => {
            handleQlSigSlot(handPopup.playerNum, idx, "defend");
            setHandPopup(null);
          }}
          onPlayQlSigAsUtility={(idx) => {
            handleQlSigSlot(handPopup.playerNum, idx, "utility");
            setHandPopup(null);
          }}
          onPlayQlSigAsStance={(idx) => {
            handleQlSigSlot(handPopup.playerNum, idx, "stance");
            setHandPopup(null);
          }}
          // Equipment actions
          onTapEquipAttack={(idx) => {
            handleEquipSlot(handPopup.playerNum, idx, "attack");
            setHandPopup(null);
          }}
          onTapEquipDefend={(idx) => {
            handleEquipSlot(handPopup.playerNum, idx, "defend");
            setHandPopup(null);
          }}
          // Armor block
          onArmorBlock={() => {
            handleArmorBlock(handPopup.playerNum);
            setHandPopup(null);
          }}
          // Bloodforged Plate — open the spend-counters prompt.
          onActivateBloodforged={() => {
            if (!game) return;
            const n = handPopup.playerNum;
            const have = game.players[n].armorBloodCounters ?? 0;
            if (have <= 0) return;
            setGame(setPendingPrompt(game, {
              kind: "bloodforged-activate",
              playerNum: n,
              maxCounters: have,
              selected: 1,
            }));
            setHandPopup(null);
          }}
          onActivateItem={(instanceId, cardDbId) => {
            if (!game) return;
            const n = handPopup.playerNum;
            setGame(activateItemCard(game, n, cardDbId, instanceId, cardMap));
            setHandPopup(null);
          }}
        />
      )}
    </div>
  );
}

// ─── Discard Pile Modal (inline) ─────────────────────────────────────────────

import type { PlayerState } from "@/types/game";

function DiscardPileModal({
  player,
  cardMap,
  onClose,
}: {
  player: PlayerState;
  cardMap: Map<string, Card>;
  onClose: () => void;
}) {
  const cards = player.discard
    .map((id) => cardMap.get(id))
    .filter((c): c is Card => Boolean(c));

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="relative max-h-[85vh] w-full max-w-4xl overflow-hidden rounded-lg border border-[#2a2f3a] bg-[#13151a] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[#2a2f3a] px-4 py-3">
          <h3 className="font-display text-base font-semibold text-amber-soft">
            {player.name} — Discard Pile ({cards.length})
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[#94a3b8] hover:bg-[#22262f] hover:text-white"
            title="Close"
          >
            ✕
          </button>
        </div>
        <div className="max-h-[calc(85vh-3.5rem)] overflow-y-auto p-4">
          {cards.length === 0 ? (
            <p className="py-8 text-center text-sm text-[#64748b]">
              No cards in the discard pile yet.
            </p>
          ) : (
            <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
              {cards.map((card, i) => (
                <BoardCard key={`${card.id}_${i}`} card={card} size="hand" />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Planning View (inline) ─────────────────────────────────────────────────

import { BoardCard } from "@/components/playtest/BoardCard";

interface PlanningViewProps {
  game: GameState;
  playerNum: 1 | 2;
  cardMap: Map<string, Card>;
  onConfirm: (toDiscardInstanceIds: string[]) => void;
}

function PlanningView({ game, playerNum, cardMap, onConfirm }: PlanningViewProps) {
  const player = game.players[playerNum];
  const [selected, setSelected] = useState<Set<string>>(new Set());

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="flex min-h-screen flex-col bg-bg p-6">
      <div className="mx-auto w-full max-w-5xl space-y-4">
        <h2 className="font-display text-xl font-semibold">
          {player.name} — Planning
        </h2>
        <p className="text-sm text-[#94a3b8]">
          Select 0+ cards to discard, then draw back up to 5.
        </p>
        <div className="grid grid-cols-5 gap-3">
          {player.handInstances.map((inst) => {
            const card = cardMap.get(inst.cardId);
            if (!card) return null;
            const isSel = selected.has(inst.id);
            return (
              <div
                key={inst.id}
                onClick={() => toggle(inst.id)}
                className={isSel ? "ring-2 ring-amber-500" : ""}
              >
                <BoardCard
                  card={card}
                  size="hand"
                  selected={isSel}
                />
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-end gap-3 pt-4">
          <span className="text-xs text-[#94a3b8]">
            {selected.size} selected — will draw {Math.max(0, 5 - (player.handInstances.length - selected.size))}
          </span>
          <Button onClick={() => onConfirm(Array.from(selected))}>
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}
